<template>
  <div v-if="visible" class="session-expiry-toast">
    <div class="toast-content">
      <div class="toast-header">
        <div class="toast-icon">
          <i class="ri-time-line"></i>
        </div>
        <div class="toast-title">
          <h6>Session Expiring Soon</h6>
          <p>Your session will expire in {{ formatTime }}</p>
        </div>
        <button @click="dismissToast" class="toast-close">
          <i class="ri-close-line"></i>
        </button>
      </div>
      
      <div class="progress-container">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: progressPercentage + '%' }"
          ></div>
        </div>
        <span class="progress-text">{{ formatTime }} remaining</span>
      </div>
      
      <div class="toast-actions">
        <button @click="extendSession" class="btn-extend">
          <i class="ri-refresh-line"></i>
          I'm still here
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SessionExpiryToast',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    totalWarningTime: {
      type: Number,
      default: 120 // Time remaining until session expires (in seconds)
    }
  },
  data() {
    return {
      timeLeft: this.totalWarningTime,
      interval: null
    };
  },
  computed: {
    progressPercentage() {
      // Show progress of time elapsed out of total time remaining until expiry
      return ((this.totalWarningTime - this.timeLeft) / this.totalWarningTime) * 100;
    },
    formatTime() {
      const minutes = Math.floor(this.timeLeft / 60);
      const seconds = this.timeLeft % 60;
      return minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}s`;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.startCountdown();
      } else {
        this.stopCountdown();
      }
    }
  },
  methods: {
    startCountdown() {
      this.timeLeft = this.totalWarningTime;
      this.interval = setInterval(() => {
        this.timeLeft--;
        if (this.timeLeft <= 0) {
          this.stopCountdown();
          this.$emit('timeout');
        }
      }, 1000);
    },
    
    stopCountdown() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    
    extendSession() {
      this.$emit('extend-session');
      this.dismissToast();
    },
    
    dismissToast() {
      this.$emit('dismiss');
    }
  },
  
  beforeDestroy() {
    this.stopCountdown();
  }
};
</script>

<style lang="scss" scoped>
.session-expiry-toast {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  width: 380px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid #e9ecef;
  animation: slideInRight 0.3s ease-out;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-content {
  padding: 20px;
}

.toast-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  background-color: #2c3d50;
  .toast-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #ff9500, #ffb347);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    flex-shrink: 0;
    
    i {
      color: white;
      font-size: 18px;
    }
  }
  
  .toast-title {
    flex: 1;
    
    h6 {
      margin: 0 0 4px 0;
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
    }
    
    p {
      margin: 0;
      font-size: 14px;
      color: #6c757d;
    }
  }
  
  .toast-close {
    background: none;
    border: none;
    color: #adb5bd;
    font-size: 18px;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f8f9fa;
      color: #6c757d;
    }
  }
}

.progress-container {
  margin-bottom: 16px;
  
  .progress-bar {
    position: relative;
    width: 100%;
    height: 8px;
    background: #f1f3f4;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
    
    .progress-fill {
      height: 100%;
      background: linear-gradient(135deg, #4db848, #049200);
      border-radius: 4px;
      transition: width 1s linear;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
        animation: shimmer 2s infinite;
      }
    }
  }
  
  .progress-text {
    font-size: 12px;
    color: #6c757d;
    font-weight: 500;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.toast-actions {
  .btn-extend {
    width: 100%;
    padding: 12px 16px;
    background: linear-gradient(135deg, #4db848, #049200);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s ease;
    
    &:hover {
      background: linear-gradient(135deg, #049200, #4db848);
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(77, 184, 72, 0.3);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    i {
      font-size: 16px;
    }
  }
}

// Responsive design
@media (max-width: 480px) {
  .session-expiry-toast {
    width: calc(100vw - 40px);
    left: 20px;
    right: 20px;
  }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
  .session-expiry-toast {
    background: #2c3e50;
    border-color: #34495e;
    
    .toast-title h6 {
      color: #ecf0f1;
    }
    
    .toast-title p {
      color: #bdc3c7;
    }
    
    .toast-close {
      color: #7f8c8d;
      
      &:hover {
        background: #34495e;
        color: #bdc3c7;
      }
    }
    
    .progress-bar {
      background: #34495e;
    }
    
    .progress-text {
      color: #bdc3c7;
    }
  }
}
</style>
